import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  CompanyApiDownload,
  CompanyAssemblyMinuteDto,
  CompanyAssemblyMinuteFilterSelects,
  CompanyAssemblyMinuteListQuery,
  CompanyAssemblyMinuteListResult,
  CompanyAssemblyMinuteRequest,
  CompanyDto,
  CompanyFileDto,
  CompanyFilterSelects,
  CompanyFormSelects,
  CompanyIncorporationDto,
  CompanyIncorporationRequest,
  CompanyLegalFormSelects,
  CompanyListQuery,
  CompanyListResult,
  CompanyRequest,
  CompanySelectOption,
  CompanyToneDto,
} from './companies-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const UPLOAD_TIMEOUT_MS = 60_000;
const LIST_PAGE_SIZE = 100;
const COMPANIES_PATH = '/companies';

@Injectable({ providedIn: 'root' })
export class CompaniesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${COMPANIES_PATH}`;

  list(query: CompanyListQuery = {}): Observable<CompanyListResult> {
    return this.safeListRequest(
      this.http.get<unknown>(this.baseUrl, {
        params: companyListParams(query),
      }),
      unwrapCompanyList,
    );
  }

  formSelects(): Promise<CompanyFormSelects> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/form-selects`),
      unwrapCompanyFormSelects,
    );
  }

  filterSelects(): Promise<CompanyFilterSelects> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`),
      unwrapCompanyFilterSelects,
    );
  }

  getById(uuid: string): Promise<CompanyDto> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      unwrapCompany,
    );
  }

  create(payload: CompanyRequest): Promise<CompanyDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      unwrapCompany,
    );
  }

  update(uuid: string, payload: CompanyRequest): Promise<CompanyDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      unwrapCompany,
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  exportCompanies(query: CompanyListQuery = {}): Promise<CompanyApiDownload> {
    return this.download(
      `${this.baseUrl}/excel`,
      companyListParams(query, false),
      'empresas.xlsx',
    );
  }

  legalFormSelects(companyUuid: string): Promise<CompanyLegalFormSelects> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/form-selects`,
      ),
      unwrapLegalFormSelects,
    );
  }

  countries(): Promise<CompanySelectOption[]> {
    return this.safeRequest(
      this.http.get<unknown>(`${this.baseUrl}/locations/countries`),
      unwrapSelectList,
    );
  }

  states(countryUuid: string): Promise<CompanySelectOption[]> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/locations/countries/${countryUuid}/states`,
      ),
      unwrapSelectList,
    );
  }

  municipalities(stateUuid: string): Promise<CompanySelectOption[]> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/locations/states/${stateUuid}/municipalities`,
      ),
      unwrapSelectList,
    );
  }

  getIncorporation(companyUuid: string): Promise<CompanyIncorporationDto | null> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/incorporation`,
      ),
      unwrapIncorporation,
    );
  }

  saveIncorporation(
    companyUuid: string,
    payload: CompanyIncorporationRequest,
    instrumentFile?: File,
    registryFile?: File,
  ): Promise<CompanyIncorporationDto> {
    const formData = legalFormData(payload, instrumentFile, registryFile);
    return this.request(
      this.http.put<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/incorporation`,
        formData,
      ),
      body => {
        const result = unwrapIncorporation(body);
        if (!result) throw new SettingsApiError('unexpected', 0);
        return result;
      },
      UPLOAD_TIMEOUT_MS,
    );
  }

  listAssemblyMinutes(
    companyUuid: string,
    query: CompanyAssemblyMinuteListQuery = {},
  ): Observable<CompanyAssemblyMinuteListResult> {
    return this.safeListRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes`,
        { params: assemblyListParams(query) },
      ),
      unwrapAssemblyList,
    );
  }

  assemblyMinuteFilterSelects(
    companyUuid: string,
  ): Promise<CompanyAssemblyMinuteFilterSelects> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes/filter-selects`,
      ),
      unwrapAssemblyFilterSelects,
    );
  }

  getAssemblyMinute(
    companyUuid: string,
    uuid: string,
  ): Promise<CompanyAssemblyMinuteDto> {
    return this.safeRequest(
      this.http.get<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes/${uuid}`,
      ),
      unwrapAssemblyMinute,
    );
  }

  createAssemblyMinute(
    companyUuid: string,
    payload: CompanyAssemblyMinuteRequest,
    instrumentFile: File,
    registryFile: File,
  ): Promise<CompanyAssemblyMinuteDto> {
    return this.request(
      this.http.post<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes`,
        legalFormData(payload, instrumentFile, registryFile),
      ),
      unwrapAssemblyMinute,
      UPLOAD_TIMEOUT_MS,
    );
  }

  updateAssemblyMinute(
    companyUuid: string,
    uuid: string,
    payload: CompanyAssemblyMinuteRequest,
    instrumentFile?: File,
    registryFile?: File,
  ): Promise<CompanyAssemblyMinuteDto> {
    return this.request(
      this.http.put<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes/${uuid}`,
        legalFormData(payload, instrumentFile, registryFile),
      ),
      unwrapAssemblyMinute,
      UPLOAD_TIMEOUT_MS,
    );
  }

  removeAssemblyMinute(companyUuid: string, uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(
        `${this.baseUrl}/${companyUuid}/legal/assembly-minutes/${uuid}`,
      ),
      () => undefined,
    );
  }

  exportAssemblyMinutes(
    companyUuid: string,
    query: CompanyAssemblyMinuteListQuery = {},
  ): Promise<CompanyApiDownload> {
    return this.download(
      `${this.baseUrl}/${companyUuid}/legal/assembly-minutes/excel`,
      assemblyListParams(query, false),
      'actas-asamblea.xlsx',
    );
  }

  private safeListRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Observable<T> {
    return source.pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => retryDelay(error) }),
      map(project),
      catchError(error => throwError(() => mapCompaniesError(error))),
    );
  }

  private safeRequest<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(
      source.pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
    timeoutMs = REQUEST_TIMEOUT_MS,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(timeoutMs)));
      return project(body);
    } catch (error) {
      throw mapCompaniesError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<CompanyApiDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, {
            params,
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapCompaniesError(error);
    }
  }
}

function companyListParams(
  query: CompanyListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.name) params = params.set('filters[name]', query.name);
  if (query.rfc) params = params.set('filters[rfc]', query.rfc);
  if (query.curp) params = params.set('filters[curp]', query.curp);
  if (query.typeUuids?.length) {
    params = params.set('select[type]', query.typeUuids.join(','));
  }
  if (query.statusUuids?.length) {
    params = params.set('select[status]', query.statusUuids.join(','));
  }
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function assemblyListParams(
  query: CompanyAssemblyMinuteListQuery,
  includePagination = true,
): HttpParams {
  let params = new HttpParams();
  if (includePagination) {
    params = params
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));
  }
  if (query.date) params = params.set('filters[date]', query.date);
  if (query.number) params = params.set('filters[number]', query.number);
  if (query.matters) params = params.set('filters[matters]', query.matters);
  if (query.issuerName) {
    params = params.set('filters[issuerName]', query.issuerName);
  }
  params = setCsvParam(params, 'select[type]', query.typeUuids);
  params = setCsvParam(params, 'select[country]', query.countryUuids);
  params = setCsvParam(params, 'select[state]', query.stateUuids);
  params = setCsvParam(
    params,
    'select[municipality]',
    query.municipalityUuids,
  );
  params = setCsvParam(
    params,
    'select[issuerType]',
    query.issuerTypeUuids,
  );
  if (query.sortBy) params = params.set('sortBy', query.sortBy);
  if (query.order) params = params.set('order', query.order);
  return params;
}

function setCsvParam(
  params: HttpParams,
  key: string,
  values: string[] | undefined,
): HttpParams {
  return values?.length ? params.set(key, values.join(',')) : params;
}

function legalFormData(
  payload: CompanyIncorporationRequest | CompanyAssemblyMinuteRequest,
  instrumentFile?: File,
  registryFile?: File,
): FormData {
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
  );
  if (instrumentFile) formData.append('instrumentFile', instrumentFile);
  if (registryFile) formData.append('registryFile', registryFile);
  return formData;
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds =
        Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      const jitterMs = Math.floor(Math.random() * 250);
      return timer(seconds * 1000 + jitterMs);
    }
    if (error.status >= 500 || error.status === 0) {
      return timer(300);
    }
  }
  return throwError(() => error);
}

function mapCompaniesError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (error instanceof HttpErrorResponse) {
    const body = asRecord(error.error);
    const businessMessage = body ? stringField(body, ['error']) : null;
    if (businessMessage) mapped.message = businessMessage;
  }
  return mapped;
}

function unwrapCompanyList(body: unknown): CompanyListResult {
  return unwrapPagedList(body, normalizeCompany);
}

function unwrapAssemblyList(body: unknown): CompanyAssemblyMinuteListResult {
  return unwrapPagedList(body, normalizeAssemblyMinuteGrid);
}

function unwrapPagedList<T>(
  body: unknown,
  normalize: (raw: unknown) => T,
): { items: T[]; total: number } {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalize), total: data.length };
  }

  const page = asRecord(data);
  if (!page) throw new SettingsApiError('unexpected', 0);
  const items = firstArray(page, ['data', 'content', 'items']).map(normalize);
  const pagination = asRecord(page['pagination']);
  const total =
    numberField(pagination, ['totalRecords']) ??
    numberField(page, ['totalElements', 'total']) ??
    items.length;
  return { items, total };
}

function unwrapCompany(body: unknown): CompanyDto {
  return normalizeCompany(requiredData(body));
}

function normalizeCompany(raw: unknown): CompanyDto {
  const record = asRecord(raw);
  const type = record ? normalizeSelect(record['type']) : null;
  const status = record ? normalizeTone(record['status']) : null;
  const uuid = record ? stringField(record, ['uuid']) : null;
  const name = record ? stringField(record, ['name', 'displayName']) : null;
  const rfc = record ? stringField(record, ['rfc']) : null;
  if (!record || !uuid || !name || !rfc || !type || !status) {
    throw new SettingsApiError('unexpected', 0);
  }

  /*
   * businessName y societyType se normalizan por el contrato de escritura.
   * El ejemplo de CompanyResponse no los documenta; esta lectura se integra
   * conscientemente sin una respuesta de ejemplo que la confirme.
   */
  return {
    uuid,
    name,
    type,
    businessName: nullableString(record['businessName']),
    firstName: nullableString(record['firstName']),
    paternalLastName: nullableString(record['paternalLastName']),
    maternalLastName: nullableString(record['maternalLastName']),
    rfc,
    curp: nullableString(record['curp']),
    societyType: normalizeSelect(record['societyType']),
    notes: nullableString(record['notes']),
    status,
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function unwrapCompanyFormSelects(body: unknown): CompanyFormSelects {
  const data = asRecord(requiredData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    personTypes: selectArray(data, ['personTypes']),
    societyTypes: selectArray(data, ['societyTypes']),
  };
}

function unwrapCompanyFilterSelects(body: unknown): CompanyFilterSelects {
  const data = asRecord(requiredData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    personTypes: selectArray(data, ['personTypes']),
    statuses: firstArray(data, ['statuses', 'status'])
      .map(normalizeTone)
      .filter((item): item is CompanyToneDto => item !== null),
  };
}

function unwrapLegalFormSelects(body: unknown): CompanyLegalFormSelects {
  const data = asRecord(requiredData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    issuerTypes: selectArray(data, ['issuerTypes']),
    assemblyTypes: selectArray(data, ['assemblyTypes']),
    countries: selectArray(data, ['countries']),
  };
}

function unwrapSelectList(body: unknown): CompanySelectOption[] {
  const data = requiredData(body);
  if (!Array.isArray(data)) throw new SettingsApiError('unexpected', 0);
  return data
    .map(normalizeSelect)
    .filter((item): item is CompanySelectOption => item !== null);
}

function unwrapIncorporation(body: unknown): CompanyIncorporationDto | null {
  const data = unwrapData(body);
  if (data === undefined || data === null) return null;
  return normalizeIncorporation(data);
}

function normalizeIncorporation(raw: unknown): CompanyIncorporationDto {
  const record = asRecord(raw);
  if (!record) throw new SettingsApiError('unexpected', 0);
  const common = normalizeLegalRecord(record);
  const constitutionDate = stringField(record, ['constitutionDate']);
  const instrumentNumber = stringField(record, ['instrumentNumber']);
  if (!constitutionDate || !instrumentNumber) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    ...common,
    constitutionDate,
    instrumentNumber,
  };
}

function unwrapAssemblyMinute(body: unknown): CompanyAssemblyMinuteDto {
  return normalizeAssemblyMinute(requiredData(body));
}

function normalizeAssemblyMinuteGrid(
  raw: unknown,
): CompanyAssemblyMinuteDto {
  const record = asRecord(raw);
  if (!record) throw new SettingsApiError('unexpected', 0);
  const uuid = stringField(record, ['uuid']);
  const date = stringField(record, ['date']);
  const number = stringField(record, ['number']);
  const type = normalizeSelect(record['type']);
  const issuerType = normalizeSelect(record['issuerType']);
  const issuerName = stringField(record, ['issuerName']);
  const country = normalizeSelect(record['country']);
  const state = normalizeSelect(record['state']);
  const municipality = normalizeSelect(record['municipality']);
  if (
    !uuid ||
    !date ||
    !number ||
    !type ||
    !issuerType ||
    !issuerName ||
    !country ||
    !state ||
    !municipality
  ) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    date,
    number,
    type,
    issuerType,
    issuerName,
    country,
    state,
    municipality,
    matters: stringArray(record['matters']),
    instrumentFile: null,
    registryDate: nullableString(record['registryDate']) ?? '',
    registryFolio: nullableString(record['registryFolio']) ?? '',
    registryFile: null,
    notes: nullableString(record['notes']),
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function normalizeAssemblyMinute(raw: unknown): CompanyAssemblyMinuteDto {
  const record = asRecord(raw);
  if (!record) throw new SettingsApiError('unexpected', 0);
  const common = normalizeLegalRecord(record);
  const date = stringField(record, ['date']);
  const number = stringField(record, ['number']);
  const type = normalizeSelect(record['type']);
  if (!date || !number || !type) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    ...common,
    date,
    number,
    type,
    matters: stringArray(record['matters']),
  };
}

function normalizeLegalRecord(record: Record<string, unknown>): {
  uuid: string;
  issuerType: CompanySelectOption;
  issuerName: string;
  country: CompanySelectOption;
  state: CompanySelectOption;
  municipality: CompanySelectOption;
  instrumentFile: CompanyFileDto | null;
  registryDate: string;
  registryFolio: string;
  registryFile: CompanyFileDto | null;
  notes: string | null;
  createdAt?: string;
  updatedAt?: string;
} {
  const uuid = stringField(record, ['uuid']);
  const issuerType = normalizeSelect(record['issuerType']);
  const issuerName = stringField(record, ['issuerName']);
  const country = normalizeSelect(record['country']);
  const state = normalizeSelect(record['state']);
  const municipality = normalizeSelect(record['municipality']);
  const registryDate = stringField(record, ['registryDate']);
  const registryFolio = stringField(record, ['registryFolio']);
  if (
    !uuid ||
    !issuerType ||
    !issuerName ||
    !country ||
    !state ||
    !municipality ||
    !registryDate ||
    !registryFolio
  ) {
    throw new SettingsApiError('unexpected', 0);
  }
  return {
    uuid,
    issuerType,
    issuerName,
    country,
    state,
    municipality,
    instrumentFile: normalizeFile(record['instrumentFile']),
    registryDate,
    registryFolio,
    registryFile: normalizeFile(record['registryFile']),
    notes: nullableString(record['notes']),
    createdAt: optionalString(record['createdAt']),
    updatedAt: optionalString(record['updatedAt']),
  };
}

function unwrapAssemblyFilterSelects(
  body: unknown,
): CompanyAssemblyMinuteFilterSelects {
  const data = asRecord(requiredData(body));
  if (!data) throw new SettingsApiError('unexpected', 0);
  return {
    assemblyTypes: selectArray(data, ['assemblyTypes']),
    issuerTypes: selectArray(data, ['issuerTypes']),
    countries: selectArray(data, ['countries']),
    states: selectArray(data, ['states']),
    municipalities: selectArray(data, ['municipalities']),
  };
}

function normalizeSelect(raw: unknown): CompanySelectOption | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid']);
  const name = stringField(record, ['name']);
  return uuid && name ? { uuid, name } : null;
}

function normalizeTone(raw: unknown): CompanyToneDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  const name = stringField(record, ['name']);
  const value = stringField(record, ['value']);
  const tone = stringField(record, ['tone']);
  return name && value && tone ? { name, value, tone } : null;
}

function normalizeFile(raw: unknown): CompanyFileDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid']);
  const name = stringField(record, ['name']);
  const contentType = stringField(record, ['contentType']);
  const size = numberField(record, ['size']);
  const url = stringField(record, ['url']);
  return uuid && name && contentType && size !== null && url
    ? { uuid, name, contentType, size, url }
    : null;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): CompanyApiDownload {
  if (!response.body) throw new SettingsApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}

function requiredData(body: unknown): unknown {
  const data = unwrapData(body);
  if (data === undefined || data === null) {
    throw new SettingsApiError('unexpected', 0);
  }
  return data;
}

function unwrapData(body: unknown): unknown {
  const record = asRecord(body);
  if (
    record &&
    'data' in record &&
    (Object.keys(record).length === 1 ||
      'message' in record ||
      'status' in record ||
      'error' in record)
  ) {
    return record['data'];
  }
  if (
    record &&
    !('data' in record) &&
    ('message' in record || 'status' in record || 'error' in record)
  ) {
    return undefined;
  }
  return body;
}

function selectArray(
  record: Record<string, unknown>,
  keys: string[],
): CompanySelectOption[] {
  return firstArray(record, keys)
    .map(normalizeSelect)
    .filter((item): item is CompanySelectOption => item !== null);
}

function firstArray(
  record: Record<string, unknown>,
  keys: string[],
): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function stringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === 'string' && !!item.trim(),
    );
  }
  return typeof value === 'string' && value.trim() ? [value] : [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(
  record: Record<string, unknown> | null,
  keys: string[],
): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function numberField(
  record: Record<string, unknown> | null,
  keys: string[],
): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
